import * as THREE from 'three'
import { useMapCoordinateStore } from '@/stores/mapCoordinateStore.ts'
import { useMapStore } from '@/stores/mapStore.ts'
import { useMapSettingsStore } from '@/stores/mapSettingsStore.ts'
import type MapCoordinate from '@/types/MapCoordinate.ts'
import { zoomFactor } from '@/enums/zoomFactor.ts'

interface Point {
  x: number
  y: number
}
interface Circle {
  center: Point
  radius: number
}
interface Arc {
  circle: Circle
  startAngle: number
  endAngle: number
}

export class MapScanRadius {
  private scene: THREE.Scene
  private scanCircles: THREE.Line[] = []
  private readonly mapCoordinateStore = useMapCoordinateStore()
  private readonly mapStore = useMapStore()
  private readonly mapSettingsStore = useMapSettingsStore()
  private lastCoordinates: Map<string, MapCoordinate> = new Map()
  private needsFullUpdate = false
  private debounceTimeout: NodeJS.Timeout | null = null
  private readonly debounceDelay = 500 // ms

  constructor(scene: THREE.Scene) {
    this.scene = scene
    this.updateScanRadii()
  }

  /* ────────────────────────── PUBLIC API ────────────────────────── */

  /** Re‑draw everything (call after data or zoom change) - debounced */
  public updateScanRadii(): void {
    // Clear any existing timeout
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout)
    }

    // Set up new debounced update
    this.debounceTimeout = setTimeout(() => {
      this.performUpdate()
      this.debounceTimeout = null
    }, this.debounceDelay)
  }

  /** Perform the actual update logic */
  private performUpdate(): void {
    // If scan ranges are disabled, clear any existing circles and return
    if (!this.mapSettingsStore.showScanRange) {
      this.clearScanRadii()
      return
    }

    // Hide scan range at quadrant zoom level and up (most zoomed out) - don't clear to avoid expensive recalculation
    if (this.mapStore.zoomFactor <= zoomFactor.Quadrant) {
      this.scanCircles.forEach((circle) => {
        circle.visible = false
      })
      return
    }

    // Show circles if they exist but are hidden
    if (this.scanCircles.length > 0) {
      this.scanCircles.forEach((circle) => {
        circle.visible = true
      })
    }

    // Force a full update when re-enabling scan ranges
    if (this.scanCircles.length === 0) {
      this.needsFullUpdate = true
    }

    const currentCoords = this.getCoordinatesWithScanRange()
    const currentMap = new Map(currentCoords.map((c) => [`${c.xPos},${c.yPos}`, c]))

    // Check if we need a full update
    if (this.needsFullUpdate || this.lastCoordinates.size !== currentMap.size) {
      this.clearScanRadii()
      this.lastCoordinates = currentMap
      this.needsFullUpdate = false

      if (currentCoords.length === 0) return
      if (currentCoords.length === 1) {
        this.drawScanCircle(currentCoords[0])
        return
      }
      this.drawCombinedScanContour(currentCoords)
      return
    }

    // Find changed coordinates
    const changedCoords: MapCoordinate[] = []
    currentMap.forEach((coord, key) => {
      const lastCoord = this.lastCoordinates.get(key)
      if (
        !lastCoord ||
        lastCoord.xPos !== coord.xPos ||
        lastCoord.yPos !== coord.yPos ||
        lastCoord.maxScanRange !== coord.maxScanRange
      ) {
        changedCoords.push(coord)
      }
    })

    // If no changes, we're done
    if (changedCoords.length === 0) return

    // Update the last coordinates map
    this.lastCoordinates = currentMap

    // If we have multiple coordinates, we need to recalculate the entire contour
    if (currentCoords.length > 1) {
      this.clearScanRadii()
      this.drawCombinedScanContour(currentCoords)
    } else {
      // For single coordinate, just update that one
      this.clearScanRadii()
      this.drawScanCircle(currentCoords[0])
    }
  }

  /** Clear meshes *and* free GPU buffers */
  public clearScanRadii(): void {
    // Cancel any pending updates when clearing
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout)
      this.debounceTimeout = null
    }
    
    this.scanCircles.forEach((obj) => {
      this.scene.remove(obj)
      obj.geometry.dispose()
      ;(obj.material as THREE.Material).dispose()
    })
    this.scanCircles = []
  }

  /** Update visibility based on zoom level */
  public applyZoomFactor(): void {
    // Hide at quadrant zoom level and up (more zoomed out), show otherwise
    const shouldBeVisible = this.mapStore.zoomFactor > zoomFactor.Quadrant
    this.scanCircles.forEach((circle) => {
      circle.visible = shouldBeVisible
    })
  }

  /* ────────────────────────── DATA GATHERING ─────────────────────── */

  private getCoordinatesWithScanRange(): MapCoordinate[] {
    const coords: MapCoordinate[] = []
    this.mapCoordinateStore.coordinates.forEach((d: any) => {
      if (d.mapCoordinate?.maxScanRange > 1) coords.push(d.mapCoordinate)
    })
    return coords
  }

  /* ────────────────────────── SIMPLE CIRCLE ──────────────────────── */

  private drawScanCircle(coordinate: MapCoordinate): void {
    const mat = new THREE.LineDashedMaterial({
      color: 0x808080,
      transparent: true,
      opacity: 0.7,
      dashSize: 1,
      gapSize: 1
    })
    const pts: THREE.Vector3[] = []
    const segs = 64
    for (let i = 0; i <= segs; ++i) {
      const θ = (i / segs) * Math.PI * 2
      pts.push(
        new THREE.Vector3(
          coordinate.xPos + Math.cos(θ) * coordinate.maxScanRange,
          coordinate.yPos + Math.sin(θ) * coordinate.maxScanRange,
          0.1
        )
      )
    }
    const line = new THREE.Line(new THREE.BufferGeometry().setFromPoints(pts), mat)
    line.name = 'scanRadius'
    this.scanCircles.push(line)
    this.scene.add(line)
    line.computeLineDistances()
  }

  /* ────────────────────────── UNION OUTLINE ──────────────────────── */

  private drawCombinedScanContour(coords: MapCoordinate[]): void {
    const circles: Circle[] = coords.map((c) => ({
      center: { x: c.xPos, y: c.yPos },
      radius: c.maxScanRange
    }))
    const arcs = this.computeBoundaryArcs(circles)
    this.drawBoundaryArcs(arcs)
  }

  /** Core geometry routine: returns only the arcs actually on the hull */
  private computeBoundaryArcs(circles: Circle[]): Arc[] {
    const arcs: Arc[] = []
    const ε = 1e-6

    circles.forEach((A, i) => {
      /* 1. quick check – is A entirely inside some other circle? */
      const covered = circles.some(
        (B, j) => i !== j && this.dist(A.center, B.center) + A.radius <= B.radius + ε
      )
      if (covered) return // skip fully hidden circle

      /* 2. collect intersection angles on A */
      const ints: number[] = []
      circles.forEach((B, j) => {
        if (i === j) return
        this.findCircleIntersections(A, B).forEach((p) => {
          const θ = this.angle(A.center, p) // already normalised to 0‥2π
          ints.push(θ)
        })
      })
      ints.sort((a, b) => a - b)

      /* 3. if no intersections – whole circumference is visible */
      if (ints.length === 0) {
        arcs.push({ circle: A, startAngle: 0, endAngle: Math.PI * 2 })
        return
      }

      /* 4. test each arc between consecutive intersections */
      for (let k = 0; k < ints.length; ++k) {
        const θ1 = ints[k]
        const θ2 = ints[(k + 1) % ints.length]
        const mid = ((θ1 + (θ2 > θ1 ? θ2 : θ2 + Math.PI * 2)) / 2) % (Math.PI * 2)
        const mp: Point = {
          x: A.center.x + Math.cos(mid) * A.radius,
          y: A.center.y + Math.sin(mid) * A.radius
        }

        const onHull = circles.every((B, j) => j === i || this.dist(mp, B.center) > B.radius - ε)
        if (onHull) arcs.push({ circle: A, startAngle: θ1, endAngle: θ2 })
      }
    })

    return arcs
  }

  private drawBoundaryArcs(arcs: Arc[]): void {
    if (arcs.length === 0) return
    const mat = new THREE.LineDashedMaterial({
      color: 0x808080,
      transparent: true,
      opacity: 0.7,
      dashSize: 1,
      gapSize: 1
    })
    const segs = 32

    arcs.forEach((a) => {
      const pts: THREE.Vector3[] = []
      const { startAngle: s, endAngle } = a
      let e = endAngle
      if (e < s) e += Math.PI * 2
      for (let i = 0; i <= segs; ++i) {
        const t = i / segs
        const θ = s + t * (e - s)
        pts.push(
          new THREE.Vector3(
            a.circle.center.x + Math.cos(θ) * a.circle.radius,
            a.circle.center.y + Math.sin(θ) * a.circle.radius,
            0.1
          )
        )
      }
      const line = new THREE.Line(new THREE.BufferGeometry().setFromPoints(pts), mat)
      this.scanCircles.push(line)
      this.scene.add(line)
      line.computeLineDistances()
    })
  }

  /* ────────────────────────── HELPER MATH ────────────────────────── */

  /** distance between points (inline, no sqrt reuse needed elsewhere) */
  private dist(p: Point, q: Point): number {
    return Math.hypot(q.x - p.x, q.y - p.y)
  }

  /** Normalised polar angle 0 ≤ θ < 2π */
  private angle(o: Point, p: Point): number {
    return (Math.atan2(p.y - o.y, p.x - o.x) + Math.PI * 2) % (Math.PI * 2)
  }

  /** Properly handles tangency (h ≈ 0 → no intersection) */
  private findCircleIntersections(A: Circle, B: Circle): Point[] {
    const d = this.dist(A.center, B.center)
    const r0 = A.radius,
      r1 = B.radius
    const ε = 1e-6

    if (d > r0 + r1 - ε) return [] // separate
    if (d < Math.abs(r0 - r1) + ε) return [] // one inside the other
    if (d < ε) return [] // same centre
    const a = (r0 * r0 - r1 * r1 + d * d) / (2 * d)
    const hSq = r0 * r0 - a * a
    if (hSq <= ε) return [] // tangent – treat as no intersection
    const h = Math.sqrt(hSq)

    const xm = A.center.x + (a * (B.center.x - A.center.x)) / d
    const ym = A.center.y + (a * (B.center.y - A.center.y)) / d
    const rx = (-h * (B.center.y - A.center.y)) / d
    const ry = (h * (B.center.x - A.center.x)) / d

    return [
      { x: xm + rx, y: ym + ry },
      { x: xm - rx, y: ym - ry }
    ]
  }
}
